import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import * as Types from 'types'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { flashErrorMessage } from 'redux-flash'
import { useHistory, useParams } from 'react-router-dom'
import { selectors } from '../reducer'
import * as actions from '../actions'
import { Path, BDActionItems } from 'config/portal'
import { BackButton, Tooltip, StatusBadge, ActionMenu } from 'components'
import { Link, Redirect } from 'react-router-dom/cjs/react-router-dom.min'
import { formatUrl, tippyPlugins } from 'utils'
import { Icon as InfoIcon } from 'images/info.svg'
import * as apiActions from 'api-actions'
import {
  BD_VIEW_SERVICE_AGREEMENT_TOOLTIP_CONTENT,
  TOAST_ERROR_CONTACT_SUPPORT,
} from 'config/notifications'
import {
  ServicesAgreementStatus,
  SigningMethod,
} from 'config/automatic-rollovers'
import BenefitDistributionOrders, {
  benefitDistributionOrderFieldMappings,
} from './BenefitDistributionOrders'
import { BDCombinedAgreementStatus } from 'config/benefit-distributions'
import {
  UploadPlanAgreementModal,
  DownloadNotAvailableModal,
  ReInitializeDocusignModal,
} from '../components'
import { ProductAgreementType } from 'config/products'
import { downloadServicesAgreementFile } from 'api'

const { hideOnEsc, hideOnPopperBlur, preventEventBubbling } = tippyPlugins

const propTypes = {
  bdPlanAgreement: Types.bdPlanAgreementsSummary.isRequired,
  clearBDplanAgreementDetails: PropTypes.func.isRequired,
  servicesAgreements: PropTypes.arrayOf(Types.servicesAgreementSummary),
  setShouldTriggerAllServicesAgreementsFetch: PropTypes.func.isRequired,
  setShouldTriggerAllBDPlanAgreementsFetch: PropTypes.func.isRequired,
  flashErrorMessageHandler: PropTypes.func.isRequired,
  bdPlanAgreements: PropTypes.arrayOf(Types.bdPlanAgreementsSummary).isRequired,
  fetchBdPlanAgreements: PropTypes.func.isRequired,
  shouldTriggerAllBDPlanAgreementsFetch: PropTypes.bool.isRequired,
}

const defaultProps = {
  bdPlanAgreement: null,
}

const BD_ORDERS_INITIAL_FILTERS = {
  status: [],
}

function BenefitDistributionsDetails({
  bdPlanAgreement,
  clearBDplanAgreementDetails,
  servicesAgreements,
  setShouldTriggerAllServicesAgreementsFetch,
  setShouldTriggerAllBDPlanAgreementsFetch,
  flashErrorMessageHandler,
  bdPlanAgreements,
  fetchBdPlanAgreements,
  shouldTriggerAllBDPlanAgreementsFetch,
}) {
  const history = useHistory()
  const { servicesAgreementId } = useParams()
  const { errorExists, setErrorExists } = useState(false)
  const [orderFilters, setOrderFilters] = useState(BD_ORDERS_INITIAL_FILTERS)
  const [orderSearch, setOrderSearch] = useState('')
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [showResendModal, setShowResendModal] = useState(false)
  const [showDownloadNotAvailableModal, setShowDownloadNotAvailableModal] =
    useState(false)

  useEffect(() => {
    return () => {
      clearBDplanAgreementDetails()
    }
  }, [])

  useEffect(() => {
    async function getBDPlanAgreements() {
      await fetchBdPlanAgreements()
      setShouldTriggerAllBDPlanAgreementsFetch(false)
    }
    if (shouldTriggerAllBDPlanAgreementsFetch) getBDPlanAgreements()
  }, [shouldTriggerAllBDPlanAgreementsFetch])

  const noBdPlanAgreement = !bdPlanAgreement
  if (noBdPlanAgreement) {
    return <Redirect to={Path.BD_SERVICES_AGREEMENTS} />
  }
  const {
    completeLegalPlanName,
    accountNumber,
    addendumStatus,
    signingMethod,
    serviceAgreementId,
    palPlanId, // This code can be uncommented when SF and MS completes there changes.
  } = bdPlanAgreement
  const serviceAgreement = servicesAgreements.find(
    (plan) => plan.name === completeLegalPlanName
  )
  const serviceAgreementID = serviceAgreement
    ? serviceAgreement.serviceAgreementID
    : null

  const agreementIdForFileDownload =
    addendumStatus === ServicesAgreementStatus.COMPLETED ||
    addendumStatus === BDCombinedAgreementStatus.COMBINED_COMPLETED
      ? serviceAgreementId
      : palPlanId

  const updatedAddendumStatus = bdPlanAgreements.find(
    (bdPlanAgreement) =>
      bdPlanAgreement.serviceAgreementId === serviceAgreementId
  )?.addendumStatus

  const handleDownloadSelect = async () => {
    try {
      await downloadServicesAgreementFile(
        agreementIdForFileDownload,
        completeLegalPlanName
      )
    } catch (e) {
      if (e.status === 422) setShowDownloadNotAvailableModal(true)
      else setErrorExists(true)
    }
  }
  if (errorExists) {
    flashErrorMessageHandler(TOAST_ERROR_CONTACT_SUPPORT)
    return <Redirect to={Path.BD_SERVICES_AGREEMENTS} />
  }

  const bdActionItems = [
    {
      externalLink: process.env.REACT_APP_ARO_FUNDING_INSTRUCTIONS_URL,
      text: BDActionItems.VIEW_FUNDING_INSTRUCTIONS,
    },
    {
      handleSelect: () => handleDownloadSelect(),
      text: BDActionItems.DOWNLOAD_COMPLETED_AGREEMENT,
    },
  ]

  return (
    <div className="notification-agreement-details-view">
      <div className="header-bar">
        <BackButton
          link={(location) => {
            return {
              pathname: Path.BD_SERVICES_AGREEMENTS,
              search: location.state?.search,
            }
          }}
        >
          Back to all benefit distributions
        </BackButton>
        <div className="header-bar-content">
          <div className="header-bar-page-title">
            <h1 className="page-title">
              Benefit distributions - {completeLegalPlanName}
            </h1>
            <br />
            <h5 className="page-subtitle">
              Account number:{' '}
              {accountNumber === '' || accountNumber === null
                ? 'N/A'
                : accountNumber}
            </h5>
            <h5 className="page-subtitle">
              Addendum status: <StatusBadge status={updatedAddendumStatus} />
            </h5>
            <Link
              to={(location) => {
                return {
                  pathname: formatUrl(
                    `${Path.SERVICES_AGREEMENTS}/${serviceAgreementID}`
                  ),
                  state: { search: location.search },
                }
              }}
              className="link-text"
              id="plan-name-link-bd"
            >
              View services agreement
              <Tooltip
                content={BD_VIEW_SERVICE_AGREEMENT_TOOLTIP_CONTENT}
                ariaLabel="View service agreement"
                plugins={[hideOnEsc, hideOnPopperBlur, preventEventBubbling]}
              >
                <InfoIcon className="info-icon" />
              </Tooltip>
            </Link>
          </div>
          <div className="services-agreements-actions">
            {(updatedAddendumStatus === ServicesAgreementStatus.COMPLETED ||
              updatedAddendumStatus ===
                BDCombinedAgreementStatus.COMBINED_COMPLETED) && (
              <>
                <ActionMenu
                  items={bdActionItems}
                  ariaLabel="View benefit distribution action items"
                />
                <button
                  type="button"
                  onClick={() =>
                    history.push({
                      pathname: '/benefit-distributions/select-payment-method',
                      state: {
                        hasConfirmed: true,
                      },
                    })
                  }
                  className="button-primary"
                >
                  {BDActionItems.INITIATE_DISTRIBUTION}
                </button>
              </>
            )}
            {(updatedAddendumStatus ===
              ServicesAgreementStatus.PENDING_SIGNATURES ||
              updatedAddendumStatus ===
                BDCombinedAgreementStatus.COMBINED_PENDING_SIGNATURES) &&
              signingMethod === SigningMethod.GENERATE_DOCUMENT && (
                <>
                  <button
                    type="button"
                    onClick={() => setShowUploadModal(true)}
                    className="button-text"
                  >
                    {BDActionItems.UPLOAD_SIGNED_AGREEMENT}
                  </button>
                </>
              )}
            {(addendumStatus === ServicesAgreementStatus.PENDING_SIGNATURES ||
              addendumStatus ===
                BDCombinedAgreementStatus.COMBINED_PENDING_SIGNATURES) &&
              signingMethod === SigningMethod.ELECTRONIC && (
                <>
                  <button
                    type="button"
                    onClick={() => setShowResendModal(true)}
                    className="button-text"
                  >
                    {BDActionItems.RESEND_DOCUSIGN_EMAIL}
                  </button>
                </>
              )}
            {(addendumStatus === ServicesAgreementStatus.PENDING_SIGNATURES ||
              addendumStatus ===
                BDCombinedAgreementStatus.COMBINED_PENDING_SIGNATURES) && (
              <>
                <button
                  type="button"
                  onClick={() => handleDownloadSelect()}
                  className="button-primary"
                >
                  {BDActionItems.DOWNLOAD_AGREEMENT}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
      <BenefitDistributionOrders
        agreementStatus={addendumStatus}
        servicesAgreementId={servicesAgreementId}
        baseFilters={BD_ORDERS_INITIAL_FILTERS}
        filters={orderFilters}
        handleFilter={(filters) => {
          setOrderFilters(filters)
        }}
        search={orderSearch}
        handleSearch={(q) => setOrderSearch(q)}
        fieldMappings={benefitDistributionOrderFieldMappings}
      />
      {showUploadModal && (
        <UploadPlanAgreementModal
          onClose={() => setShowUploadModal(false)}
          servicesAgreement={bdPlanAgreement}
          onSubmitSuccess={() => {
            setShowUploadModal(false)
            setShouldTriggerAllServicesAgreementsFetch(true)
            setShouldTriggerAllBDPlanAgreementsFetch(true)
          }}
          agreementsTypes={ProductAgreementType.BD}
        />
      )}

      {showResendModal && (
        <ReInitializeDocusignModal
          agreementId={
            addendumStatus === ServicesAgreementStatus.PENDING_SIGNATURES
              ? serviceAgreementId
              : palPlanId
          }
          onClose={() => setShowResendModal(false)}
          productAgreementType={ProductAgreementType.BD}
          status={addendumStatus}
        />
      )}
      {showDownloadNotAvailableModal && (
        <DownloadNotAvailableModal
          onClose={() => setShowDownloadNotAvailableModal(false)}
        />
      )}
    </div>
  )
}

function mapStateToProps(state) {
  return {
    bdPlanAgreement: selectors.bdPlanAgreement(state),
    servicesAgreements: selectors.servicesAgreements(state),
    bdPlanAgreements: selectors.bdPlanAgreements(state),
    shouldTriggerAllBDPlanAgreementsFetch:
      selectors.shouldTriggerAllBDPlanAgreementsFetch(state),
  }
}

const mapDispatchToProps = {
  fetchBdPlanAgreements: apiActions.fetchBdPlanAgreements,
  clearBDplanAgreementDetails: actions.clearBDplanAgreementDetails,
  setShouldTriggerAllServicesAgreementsFetch:
    actions.setShouldTriggerAllServicesAgreementsFetch,
  setShouldTriggerAllBDPlanAgreementsFetch:
    actions.setShouldTriggerAllBDPlanAgreementsFetch,
  flashErrorMessageHandler: flashErrorMessage,
}

BenefitDistributionsDetails.propTypes = propTypes
BenefitDistributionsDetails.defaultProps = defaultProps

export default compose(connect(mapStateToProps, mapDispatchToProps))(
  BenefitDistributionsDetails
)
